import { sessionRepository } from '../repositories/session';
import { SessionRepository } from '../repositories/session/SessionRepository';
import configuration from './configuration';

export const INTERCOM_EVENTS = {
  user: { on_sign_up: `on_sign_up` },
  organization: {
    onboarding_page_view: `onboarding_page_view`,
    onboarding_done: `onboarding_done`,
    organization_cancel_subscription: `organization_cancel_subscription`,
  },
  plans: {
    search: `plans_search`,
  },
  project: {
    communication: {
      edit_sms: `project_communication_edit_sms`,
      edit_email: `project_communication_edit_email`,
      enable_sms_request: `project_communication_enable_sms_request`,
    },
    routes_exceeded: `project_routes_exceeded`,
  },
  plan: {
    created: `plan_created`,
    optimized: `plan_optimized`,
    dispatched: `plan_dispatched`,
    dispatch_copy_link: `plan_dispatch_copy_link`,
    dispatch_open_link: `plan_dispatch_open_link`,
    dispatch_send_whatsapp: `plan_dispatch_send_whatsapp`,
    dispatch_send_email: `plan_dispatch_send_email`,
    dispatch_send_email_to_all: `plan_dispatch_send_email_to_all`,
    overview: {
      map: {
        contextual_clicked: `plan_overview_map_contextual_clicked`,
      },
    },
    service: {
      created: `plan_service_created`,
    },
    route: {
      created: `plan_route_created`,
      optimized: `plan_route_optimized`,
    },
    geocode_credit_finished: `plan_geocode_credit_finished`,
  },
  assets: {
    vehicles: {
      created: `assets_vehicle_created`,
    },
    clients: {
      created: `assets_client_created`,
    },
  },
};

export const initIntercom = () => {
  // @ts-expect-error
  window.Intercom(`boot`, {
    app_id: configuration.INTERCOM_APP_ID,
  });
};

/**
 * Intercom JS API: https://developers.intercom.com/installing-intercom/docs/intercom-javascript
 */
export default class Intercom {
  private startedTours: string[] = [];
  private startedChecklists: string[] = [];

  constructor(private sessionRepository: SessionRepository) {}

  boot() {
    initIntercom();
  }

  setUser(data: any) {
    if (!this.sessionRepository.getAsUser()) {
      // @ts-expect-error
      const currentSettings = JSON.stringify(window.intercomSettings);
      const newSettings = {
        // @ts-expect-error
        ...window.intercomSettings,
        ...data,
        app_id: configuration.INTERCOM_APP_ID,
        api_base: `https://api-iam.intercom.io`,
      };

      // @ts-expect-error
      window.intercomSettings = newSettings;

      if (JSON.stringify(newSettings) !== currentSettings) {
        // @ts-expect-error
        window.Intercom(`update`);
      }
    }
  }

  track(evt: string, meta: any = {}) {
    // @ts-expect-error
    window.Intercom(`trackEvent`, evt, meta);

    // This is needed in order to refresh the intercom data.
    // More info here: https://www.intercom.com/help/en/articles/170-integrate-intercom-in-a-single-page-app
    setTimeout(() => {
      // @ts-expect-error
      window.Intercom(`update`, { last_request_at: Number.parseInt(new Date().getTime() / 1000, 10) });
    }, 800);
  }

  logout() {
    // @ts-expect-error
    window.Intercom(`shutdown`);
  }

  /**
   * Open the intercom widget
   *
   * @param text string
   */
  open(text = ``) {
    // @ts-expect-error
    if (text !== ``) window.Intercom(`show`, text);
    // @ts-expect-error
    else window.Intercom(`show`);
  }

  /**
   * The widget will be opened and a message will be automatically
   * sent on behalf of the user of your website
   * @param textMessage Message to send via Intercom chat.
   */
  message(textMessage: string) {
    // @ts-expect-error
    window.Intercom(`showNewMessage`, textMessage);
  }

  /**
   * Starts an Intercom tour
   * @param id Tour ID.
   */
  startTour(id: string) {
    if (!this.startedTours.includes(id)) {
      // @ts-expect-error
      window.Intercom(`startTour`, id);
      this.startedTours.push(id);
    }
  }

  /**
   * Starts an Intercom checklist
   * @param id Checklist ID.
   */
  startChecklist(id: string) {
    if (!this.startedChecklists.includes(id)) {
      // @ts-expect-error
      window.Intercom(`startChecklist`, id);
      this.startedChecklists.push(id);
    }
  }
}

export const intercom = new Intercom(sessionRepository);
